import type { IWedding } from '@bridebook/models/source/models/Weddings.types';

export type WeddingFeatures = {
  wedding_country?: string;
  wedding_region?: string;
  wedding_subregion?: string;
  wedding_has_website?: boolean;
  wedding_actual_costs?: number;
  wedding_estimated_costs?: number;
  wedding_no_estimated_guests?: number;
  wedding_budget?: number;
  wedding_no_notes?: number;
  wedding_no_completed_tasks?: number;
  wedding_no_total_tasks?: number;
};

type WeddingLike = {
  location?: IWedding['location'];
  budget?: IWedding['budget'];
  guests?: Partial<IWedding['guests']>;
  costs?: Partial<IWedding['costs']>;
  notes?: Partial<IWedding['notes']>;
  website?: Partial<IWedding['website']>;
  tasks?: { done?: number; total?: number };
};

/**
 * Build wedding feature map from a wedding doc (or Redux profile, same shape).
 * Undefined/null values are passed through so callers can strip them at the wire
 * boundary if needed.
 */
export const buildWeddingFeatures = (wedding: WeddingLike): WeddingFeatures => ({
  wedding_country: wedding.location?.country,
  wedding_region: wedding.location?.adminArea?.[1],
  wedding_subregion: wedding.location?.adminArea?.[0],
  wedding_has_website: Boolean(wedding.website?.id),
  wedding_actual_costs: wedding.costs?.actual,
  wedding_estimated_costs: wedding.costs?.estimate,
  wedding_no_estimated_guests: wedding.guests?.estimate ?? undefined,
  wedding_budget: wedding.budget ?? undefined,
  wedding_no_notes: wedding.notes?.count,
  wedding_no_completed_tasks: wedding.tasks?.done,
  wedding_no_total_tasks: wedding.tasks?.total,
});

/**
 * Drop undefined and false entries from a wedding features map. Both represent "feature absent",
 * so skipping them keeps query strings / payloads compact without changing semantics.
 */
export const compactWeddingFeatures = (features: WeddingFeatures): WeddingFeatures =>
  Object.fromEntries(Object.entries(features).filter(([, v]) => v !== undefined && v !== false));

/**
 * Convenience helper combining `buildWeddingFeatures` and `compactWeddingFeatures`. Returns a
 * wire-ready feature map (no undefined/false entries).
 */
export const buildCompactWeddingFeatures = (wedding: WeddingLike): WeddingFeatures =>
  compactWeddingFeatures(buildWeddingFeatures(wedding));
